import DBModal from 'components/modal/modal';
import styles from './manualTxModal.module.scss';
import {Alert, Form, Input, InputNumber, Select} from 'antd';
import {currencySymbols, paymentProviders, walletCurrencies} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useCallback, useEffect, useState} from 'react';
import {Button} from '@dabafinance/react-components';
import {useLazyQuery, useMutation} from '@apollo/client';
import {
  CREATE_DEPOSIT_TX,
  CREATE_WITHDRAWAL_TX,
} from 'graphql/mutations/wallet';
import {DEPOSIT_SIM_WALLET} from 'graphql/queries/wallet';
import {GET_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {toast} from 'react-toastify';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {walletTransactionDirectionOptions} from 'utils/constants';
import FileUploadList from 'components/fileUploadList';
import ManualTxReasonField from './ManualTxReasonField';
import {NO_REASON_GIVEN} from './manualTxReasons';
import {VALIDATE_COUPON} from 'pages/AcademyCodes/resolvers';
import {debounce} from 'utils/helper';

const DABA_PRO_PLAN = [
  {value: 'THREE_MONTHS', label: '3 Months'},
  {value: 'SIX_MONTHS', label: '6 Months'},
  {value: 'ONE_YEAR', label: '1 Year'},
];

/**
 * Mirror of TRANSFER_ID_GUARDED_PROVIDERS in
 * users/src/utils/providerTransferId.js — the bank-rail providers whose
 * transactions are guarded by the duplicate-credit registry. When one of
 * these is selected, the manual modal REQUIRES a transfer id up front so a
 * duplicate wire (or a wire an admin also queued via the standard approval
 * flow) cannot be recorded twice. Keep this list in sync with the server
 * side — the resolver will reject a manual create without a transferId when
 * a guarded provider is picked, so drift here surfaces as a confusing error
 * rather than a bypass.
 */
const TRANSFER_ID_GUARDED_PROVIDERS = [
  'WISE',
  'REVOLUT',
  'WIRE_TRANSFER',
  'BANK_ACCOUNT',
  'DWOLLA',
  'INTERAC',
];

// Mirror of TRANSFER_ID_GUARDED_PLATFORM_LEDGER_DIRECTIONS. PLATFORM is also
// used for Academy sales, Pro subscriptions, balance adjustments and liens,
// which have no wire behind them, so it is only guarded on a bank movement.
const TRANSFER_ID_GUARDED_PLATFORM_DIRECTIONS = [
  'USER_BANK_ACCOUNT_TO_USER_WALLET',
  'USER_WALLET_TO_USER_BANK_ACCOUNT',
  'DEPOSIT',
  'WITHDRAWAL',
];

const requiresTransferIdFor = (provider, direction) => {
  const key = String(provider || '').toUpperCase();
  if (TRANSFER_ID_GUARDED_PROVIDERS.includes(key)) return true;
  return (
    key === 'PLATFORM' &&
    TRANSFER_ID_GUARDED_PLATFORM_DIRECTIONS.includes(
      String(direction || '').toUpperCase(),
    )
  );
};

const ACADEMY_DELIVERY_OPTIONS = [
  {value: 'ONLINE_COURSE', label: 'Online Course'},
  {value: 'PDF_BOOK', label: 'PDF Book'},
];

const Section = ({title, description, accent, children}) => (
  <section className={`${styles.section} ${accent ? styles.accent : ''}`}>
    <div className={`${styles.sectionHead} ${accent ? styles.accentHead : ''}`}>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

/** Never show a raw enum to an admin. */
const humanize = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const directionLabel = value =>
  walletTransactionDirectionOptions.find(option => option.value === value)
    ?.label || humanize(value);

const ownerName = owner =>
  `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim() ||
  owner?.email ||
  'Unknown';

const ManualTxModal = ({
  isOpen,
  onClose,
  type,
  userId,
  refetch,
  isSimMode,
  refetchUserWallet,
}) => {
  const [formValues, setFormValues] = useState({});
  const [showConfirmPage, setShowConfirmPage] = useState(false);
  const [user, setUser] = useState('');
  const [form] = Form.useForm();
  const amount = Form.useWatch('amount', form);
  const fees = Form.useWatch('fees', form);
  const direction = Form.useWatch('direction', form);
  const currency = Form.useWatch('currency', form);
  const subscriptionType = Form.useWatch('subscriptionType', form);
  const couponCode = Form.useWatch('couponCode', form);
  const originalAmount = Form.useWatch('originalAmount', form);
  const paymentProvider = Form.useWatch('paymentProvider', form);
  const providerRequiresTransferId =
    !isSimMode && requiresTransferIdFor(paymentProvider, direction);
  const providerLabel =
    paymentProviders.find(p => p.key === paymentProvider)?.label ||
    paymentProvider;
  const [couponState, setCouponState] = useState({status: 'idle'});

  const [createManualDepositWalletTransaction, {loading: loadingDeposit}] =
    useMutation(CREATE_DEPOSIT_TX, {
      onError: error => toast.error(error.message),
    });

  const [creditSimWallet, {loading: loadingSimDeposit}] = useMutation(
    DEPOSIT_SIM_WALLET,
    {
      onError: error => toast.error(error.message),
    },
  );

  const [
    createManualWithdrawalWalletTransaction,
    {loading: loadingWithdrawal},
  ] = useMutation(CREATE_WITHDRAWAL_TX, {
    onError: error => toast.error(error.message),
  });

  const [validateCoupon] = useLazyQuery(VALIDATE_COUPON, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const [fetchAppConfig, {data: appConfigData}] = useLazyQuery(
    GET_APP_CONFIGURATION,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    form.resetFields();
    // Clear DABA_PRO and DABA_ACADEMY if type is not WITHDRAWAL
    if (type !== 'WITHDRAWAL') {
      form.setFieldValue('direction', undefined);
      form.setFieldValue('subscriptionType', undefined);
    }
  }, [type, form]);

  useEffect(() => {
    if (amount && direction !== 'DABA_PRO' && direction !== 'DABA_ACADEMY') {
      form.setFieldValue(
        'total',
        type === 'DEPOSIT' ? amount + fees : amount - fees,
      );
    }
  }, [amount, fees, direction, type, form]);

  useEffect(() => {
    if (direction === 'DABA_ACADEMY' || direction === 'DABA_PRO') {
      fetchAppConfig();
    }
    // Clear subscriptionType when direction changes away from DABA_PRO
    if (direction !== 'DABA_PRO') {
      form.setFieldValue('subscriptionType', undefined);
    }
  }, [direction, fetchAppConfig, form]);

  useEffect(() => {
    if (
      direction === 'DABA_ACADEMY' &&
      currency &&
      appConfigData?.getAppConfig?.config?.dabaSubscription?.academyPlanPrices
    ) {
      const academyPrices =
        appConfigData.getAppConfig.config.dabaSubscription.academyPlanPrices;
      const matchingPrice = academyPrices.find(
        price => price.currency === currency,
      );
      if (matchingPrice && matchingPrice.amount) {
        form.setFieldValue('amount', matchingPrice.amount);
        // `originalAmount` is the list price; `amount` is what was actually
        // charged, so a discount reads as the gap between the two.
        form.setFieldValue('originalAmount', matchingPrice.amount);
      }
    }
  }, [direction, currency, appConfigData, form]);

  useEffect(() => {
    if (
      direction === 'DABA_PRO' &&
      currency &&
      subscriptionType &&
      appConfigData?.getAppConfig?.config?.dabaSubscription?.proPlanPrices
    ) {
      const proPrices =
        appConfigData.getAppConfig.config.dabaSubscription.proPlanPrices;
      const matchingPrice = proPrices.find(
        price =>
          price.currency === currency &&
          price.subscriptionType === subscriptionType,
      );
      if (matchingPrice && matchingPrice.amount) {
        form.setFieldValue('amount', matchingPrice.amount);
      }
    }
  }, [direction, currency, subscriptionType, appConfigData, form]);

  const runCouponCheck = useCallback(
    debounce(code => {
      validateCoupon({variables: {coupon: code}})
        .then(({data}) => {
          const response = data?.getCoupon;
          if (!response || response.__typename === 'Error') {
            setCouponState({
              status: 'invalid',
              message:
                response?.message ||
                'This promo code could not be found or has expired',
            });
            return;
          }
          const coupon = response.data;
          const ownerType = coupon?.ownerType || 'PLATFORM';
          if (ownerType === 'PLATFORM' || !coupon?.owner) {
            setCouponState({status: 'unowned'});
            return;
          }
          setCouponState({status: 'owned', ownerType, owner: coupon.owner});
        })
        .catch(() =>
          setCouponState({
            status: 'invalid',
            message: 'Could not validate this promo code, please try again',
          }),
        );
    }),
    [validateCoupon],
  );

  useEffect(() => {
    if (direction !== 'DABA_ACADEMY') {
      setCouponState({status: 'idle'});
      return;
    }
    const code = (couponCode || '').trim();
    if (!code) {
      setCouponState({status: 'idle'});
      return;
    }
    // Mark it checking immediately so the previous verdict can never be
    // mistaken for a verdict on the code currently in the box.
    setCouponState({status: 'checking'});
    runCouponCheck(code);
  }, [couponCode, direction, runCouponCheck]);

  const isAcademy = !isSimMode && direction === 'DABA_ACADEMY';
  const hasCouponCode = Boolean((couponCode || '').trim());
  // A mistyped code silently produces no attribution and no commission, and
  // the failure is invisible until payout weeks later — so block the submit.
  const couponBlocksSubmit =
    isAcademy &&
    hasCouponCode &&
    (couponState.status === 'checking' || couponState.status === 'invalid');

  const handleSubmit = () => {
    const {
      paymentProvider,
      currency,
      amount,
      direction,
      reason,
      fees,
      files,
      couponCode,
      originalAmount,
      preferedDelivery,
      transferId,
    } = formValues;

    // Use simulator mutation for deposits in sim mode
    if (isSimMode && type === 'DEPOSIT') {
      creditSimWallet({
        variables: {
          input: {
            userId: userId ?? user?.split(';')[2],
            currency,
            amount: Number(amount),
          },
        },
      })
        .then(({data}) => {
          if (data?.creditSimWallet?.__typename === 'Error') {
            toast.error(data.creditSimWallet.message);
          } else {
            toast.success('Simulator Deposit Created Successfully');
            setShowConfirmPage(false);
            form.resetFields();
            refetch?.();
            refetchUserWallet?.();
            onClose();
            setUser('');
          }
        })
        .catch(error => {
          toast.error(error?.message || 'An error occurred');
        });
      return;
    }

    // Live mode mutations
    const resolver =
      type === 'DEPOSIT'
        ? createManualDepositWalletTransaction
        : createManualWithdrawalWalletTransaction;

    // Only send transferId when the picked provider is guarded — sending it
    // on an unguarded manual create would just add noise to the tx metadata.
    const providerIsGuarded = requiresTransferIdFor(paymentProvider, direction);

    resolver({
      variables: {
        input: {
          userId: userId ?? user?.split(';')[2],
          currency,
          direction,
          paymentProvider,
          ...(providerIsGuarded && transferId?.trim()
            ? {transferId: transferId.trim()}
            : {}),
          // Blank is allowed in the form but not on the wire — see
          // NO_REASON_GIVEN.
          reason: String(reason ?? '').trim() || NO_REASON_GIVEN,
          amount: Number(amount),
          fees: Number(fees),
          metadata: {
            ...(files && files.length > 0 ? {files} : {}),
            // The coupon is the only link between an Academy sale and the
            // agent or partner who earned it.
            ...(direction === 'DABA_ACADEMY'
              ? {
                  ...(couponCode?.trim()
                    ? {couponCode: couponCode.trim()}
                    : {}),
                  ...(originalAmount !== undefined && originalAmount !== null
                    ? {originalAmount: Number(originalAmount)}
                    : {}),
                  ...(preferedDelivery ? {preferedDelivery} : {}),
                }
              : {}),
          },
        },
      },
    })
      .then(({data}) => {
        const responseKey =
          type === 'DEPOSIT'
            ? 'createManualDepositWalletTransaction'
            : 'createManualWithdrawalWalletTransaction';
        const response = data?.[responseKey];
        if (response?.__typename === 'Error') {
          toast.error(response.message);
        } else {
          toast.success('Created Successfully');
          setShowConfirmPage(false);
          form.resetFields();
          refetch?.();
          refetchUserWallet?.();
          onClose();
          setUser('');
        }
      })
      .catch(error => {
        toast.error(error?.message || 'An error occurred');
      });
  };

  const money = value =>
    `${currencySymbols[currency]?.symbol || ''}${Number(
      value || 0,
    ).toLocaleString()}`;

  const confirmMoney = value =>
    `${currencySymbols[formValues?.currency]?.symbol || ''}${Number(
      value || 0,
    ).toLocaleString()}`;

  const discount = Number(originalAmount || 0) - Number(amount || 0);
  const submitting = loadingDeposit || loadingWithdrawal || loadingSimDeposit;

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={onClose}
      width={820}
      content={
        <div className={styles.root}>
          <div className={styles.header}>
            <div>
              <h2>
                {showConfirmPage ? 'Confirm' : 'Create'}{' '}
                {isSimMode ? 'Simulator' : 'Manual'}{' '}
                {type === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'}
              </h2>
              <p>
                {showConfirmPage
                  ? 'Check the details below before this is recorded.'
                  : isAcademy
                  ? 'Recording an Academy course sale. The promo code decides who earns commission on it.'
                  : "Record a transaction against this user's wallet."}
              </p>
            </div>
            {isSimMode && <span className={styles.simBadge}>SIMULATOR</span>}
          </div>

          {showConfirmPage ? (
            <>
              <div className={styles.confirmBody}>
                <p className={styles.confirmLead}>
                  {isSimMode ? (
                    <>
                      You are about to credit{' '}
                      <strong>{confirmMoney(formValues?.amount)}</strong> to the
                      user&apos;s simulator wallet.
                    </>
                  ) : type === 'DEPOSIT' ? (
                    <>
                      You are about to record a deposit of{' '}
                      <strong>
                        {confirmMoney(
                          Number(formValues?.amount || 0) +
                            Number(formValues?.fees || 0),
                        )}
                      </strong>
                      .
                    </>
                  ) : (
                    <>
                      You are about to withdraw{' '}
                      <strong>{confirmMoney(formValues?.amount)}</strong> from
                      this user&apos;s wallet.
                    </>
                  )}
                </p>

                <div className={styles.confirmCard}>
                  {!isSimMode && (
                    <div className={styles.confirmRow}>
                      <span>Direction</span>
                      <span>
                        {directionLabel(formValues?.direction) || '-'}
                      </span>
                    </div>
                  )}
                  {!isSimMode &&
                    requiresTransferIdFor(
                      formValues?.paymentProvider,
                      formValues?.direction,
                    ) &&
                    formValues?.transferId && (
                      <div className={styles.confirmRow}>
                        <span>
                          {paymentProviders.find(
                            p => p.key === formValues?.paymentProvider,
                          )?.label || formValues?.paymentProvider}{' '}
                          transfer id
                        </span>
                        <span>{formValues.transferId}</span>
                      </div>
                    )}
                  <div className={styles.confirmRow}>
                    <span>
                      {type === 'DEPOSIT'
                        ? 'Credited to user'
                        : 'Amount charged'}
                    </span>
                    <span>{confirmMoney(formValues?.amount)}</span>
                  </div>
                  {!isSimMode && (
                    <div className={styles.confirmRow}>
                      <span>Fees</span>
                      <span>{confirmMoney(formValues?.fees)}</span>
                    </div>
                  )}
                  {!isSimMode && (
                    <div className={styles.confirmRow}>
                      <span>
                        {type === 'DEPOSIT'
                          ? 'Total deposit (inclusive of fees)'
                          : 'User receives'}
                      </span>
                      <span>
                        {confirmMoney(
                          type === 'DEPOSIT'
                            ? Number(formValues?.amount || 0) +
                                Number(formValues?.fees || 0)
                            : Number(formValues?.amount || 0) -
                                Number(formValues?.fees || 0),
                        )}
                      </span>
                    </div>
                  )}
                  {formValues?.direction === 'DABA_ACADEMY' &&
                    formValues?.preferedDelivery && (
                      <div className={styles.confirmRow}>
                        <span>Preferred delivery</span>
                        <span>{humanize(formValues.preferedDelivery)}</span>
                      </div>
                    )}
                  {formValues?.direction === 'DABA_ACADEMY' &&
                    Number(formValues?.originalAmount || 0) >
                      Number(formValues?.amount || 0) && (
                      <div className={styles.confirmRow}>
                        <span>Discount off list price</span>
                        <span>
                          {confirmMoney(
                            Number(formValues.originalAmount) -
                              Number(formValues.amount),
                          )}{' '}
                          off {confirmMoney(formValues.originalAmount)}
                        </span>
                      </div>
                    )}
                </div>

                {formValues?.direction === 'DABA_ACADEMY' && (
                  <div className={styles.attribution}>
                    {formValues?.couponCode ? (
                      <Alert
                        type={
                          couponState.status === 'owned' ? 'success' : 'info'
                        }
                        showIcon
                        message={
                          couponState.status === 'owned'
                            ? `Attributed to ${ownerName(couponState.owner)}`
                            : 'Platform discount code — no attribution'
                        }
                        description={`Promo code ${formValues.couponCode}`}
                      />
                    ) : (
                      <Alert
                        type="warning"
                        showIcon
                        message="No promo code — this sale will not be attributed to anyone."
                      />
                    )}
                  </div>
                )}
              </div>

              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.cancel}
                  onClick={() => setShowConfirmPage(false)}>
                  Back
                </button>
                <Button
                  disabled={submitting}
                  label={submitting ? 'Saving…' : 'Confirm'}
                  onClick={handleSubmit}
                />
              </div>
            </>
          ) : (
            <Form
              onFinish={values => {
                setShowConfirmPage(true);
                setFormValues(values);
              }}
              form={form}
              layout="vertical"
              style={{display: 'flex', flexDirection: 'column', minHeight: 0}}>
              <div className={styles.body}>
                <Section
                  title="Transaction"
                  description="Who this is for, and what kind of movement it is.">
                  <div className={styles.grid}>
                    {!userId && (
                      <Form.Item
                        name="user"
                        className={styles.full}
                        label="Select User">
                        <UserSelectField
                          mode="single"
                          users={user}
                          defaultUsers={user?.split(';')[2]}
                          setUsers={setUser}
                        />
                      </Form.Item>
                    )}

                    {!isSimMode && (
                      <Form.Item
                        label="Payment Provider"
                        rules={[
                          {required: true, message: 'This is a Required Field'},
                        ]}
                        name="paymentProvider">
                        <Select allowClear placeholder="Select provider">
                          {paymentProviders.map(data => (
                            <Select.Option key={data?.key}>
                              {data?.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}

                    {providerRequiresTransferId && (
                      <Form.Item
                        label={`${providerLabel} Transfer ID`}
                        name="transferId"
                        extra={`Read from the ${providerLabel} receipt exactly as shown. Prevents the same wire from being recorded twice — a repeated id is rejected by the platform's duplicate-guard.`}
                        rules={[
                          {
                            required: true,
                            message: `A ${providerLabel} transfer id is required for manual ${
                              type === 'DEPOSIT' ? 'deposits' : 'withdrawals'
                            } via this provider`,
                          },
                        ]}>
                        <Input
                          allowClear
                          placeholder={
                            paymentProvider === 'WISE'
                              ? 'e.g. 2378732506 or TRANSFER-2378732506'
                              : 'e.g. reference from the payment receipt'
                          }
                        />
                      </Form.Item>
                    )}

                    <Form.Item
                      rules={[
                        {required: true, message: 'This is a Required Field'},
                      ]}
                      name="currency"
                      label="Currency">
                      <Select allowClear placeholder="Select currency">
                        {walletCurrencies.map(data => (
                          <Select.Option key={data?.key}>
                            {data?.key}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {!isSimMode && (
                      <Form.Item
                        label="Direction"
                        rules={[
                          {required: true, message: 'This is a Required Field'},
                        ]}
                        name="direction">
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select direction">
                          {walletTransactionDirectionOptions
                            .filter(
                              data =>
                                type === 'WITHDRAWAL' ||
                                (data.value !== 'DABA_PRO' &&
                                  data.value !== 'DABA_ACADEMY'),
                            )
                            .map(data => (
                              <Select.Option key={data.value}>
                                {data.label}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>
                    )}

                    {!isSimMode && direction === 'DABA_PRO' && (
                      <Form.Item
                        label="Subscription Type"
                        rules={[
                          {required: true, message: 'This is a Required Field'},
                        ]}
                        name="subscriptionType">
                        <Select
                          allowClear
                          placeholder="Select subscription type">
                          {DABA_PRO_PLAN.map(plan => (
                            <Select.Option key={plan.value} value={plan.value}>
                              {plan.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </div>
                </Section>

                {isAcademy && (
                  <Section
                    accent
                    title="Academy Sale Attribution"
                    description="The promo code is the only link between this sale and the agent or partner who earned it. A mistyped code silently pays nobody, and nothing surfaces until payout weeks later.">
                    <div className={styles.grid}>
                      <Form.Item
                        label="Promo Code Used at Checkout"
                        name="couponCode"
                        extra="Leave empty only if the buyer genuinely used no code."
                        validateStatus={
                          couponState.status === 'invalid' ? 'error' : ''
                        }>
                        <Input allowClear placeholder="e.g. AMBASSADOR20" />
                      </Form.Item>

                      <Form.Item
                        label="Preferred Delivery"
                        name="preferedDelivery">
                        <Select allowClear placeholder="Select delivery format">
                          {ACADEMY_DELIVERY_OPTIONS.map(option => (
                            <Select.Option
                              key={option.value}
                              value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <div className={styles.full} style={{marginBottom: 16}}>
                        {!hasCouponCode && (
                          <Alert
                            type="warning"
                            showIcon
                            message="No promo code entered — this sale will not be attributed to any sales agent or referral partner."
                          />
                        )}
                        {hasCouponCode && couponState.status === 'checking' && (
                          <Alert
                            type="info"
                            showIcon
                            message="Checking this promo code…"
                          />
                        )}
                        {hasCouponCode && couponState.status === 'owned' && (
                          <Alert
                            type="success"
                            showIcon
                            message={`This sale will be attributed to ${ownerName(
                              couponState.owner,
                            )}`}
                            description={
                              couponState.ownerType === 'AGENT'
                                ? 'Sales agent — the transaction is claimed for reporting only, no commission is paid.'
                                : 'Referral partner — they earn commission on this sale if their Academy commission is enabled.'
                            }
                          />
                        )}
                        {hasCouponCode && couponState.status === 'unowned' && (
                          <Alert
                            type="info"
                            showIcon
                            message="Valid platform discount code"
                            description="This code has no owner, so the sale is recorded but attributed to nobody. That is normal for a plain platform discount."
                          />
                        )}
                        {hasCouponCode && couponState.status === 'invalid' && (
                          <Alert
                            type="error"
                            showIcon
                            message="This promo code is not valid"
                            description={couponState.message}
                          />
                        )}
                      </div>
                    </div>
                  </Section>
                )}

                <Section
                  title="Amounts"
                  description={
                    isAcademy
                      ? 'The amount charged is what the buyer actually paid; the list price is what it costs without a discount.'
                      : undefined
                  }>
                  <div className={styles.grid}>
                    <Form.Item
                      rules={[
                        {required: true, message: 'This is a Required Field'},
                        {
                          validator: (_, value) =>
                            value > 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error('Amount must be greater than zero'),
                                ),
                        },
                      ]}
                      name="amount"
                      label={
                        isSimMode
                          ? 'Simulator Deposit Amount'
                          : isAcademy
                          ? 'Amount Charged'
                          : type === 'DEPOSIT'
                          ? 'Deposit Amount Credited to User (Net of Fees)'
                          : 'Total Withdrawal Amount (Including Fees)'
                      }>
                      <InputNumber
                        className="input-number"
                        style={{width: '100%'}}
                        min={0}
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>

                    {isAcademy && (
                      <Form.Item
                        label="List Price Before Discount"
                        name="originalAmount"
                        extra="Defaults to the configured Academy list price.">
                        <InputNumber
                          className="input-number"
                          style={{width: '100%'}}
                          min={0}
                          formatter={value =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    )}

                    {isAcademy && discount > 0 && (
                      <div className={styles.discountNote}>
                        Discount applied: <strong>{money(discount)}</strong> off
                        a list price of <strong>{money(originalAmount)}</strong>
                        .
                      </div>
                    )}

                    {!isSimMode && (
                      <Form.Item
                        rules={[
                          {required: true, message: 'This is a Required Field'},
                          {
                            validator: (_, value) =>
                              value >= 0
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error('Fee cannot be less than zero'),
                                  ),
                          },
                        ]}
                        name="fees"
                        label="Transaction Fees">
                        <InputNumber
                          className="input-number"
                          style={{width: '100%'}}
                          formatter={value =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          min={0}
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    )}

                    {!isSimMode &&
                      direction !== 'DABA_PRO' &&
                      direction !== 'DABA_ACADEMY' && (
                        <Form.Item
                          name="total"
                          label={
                            type === 'DEPOSIT'
                              ? 'Total Deposit Amount (Inclusive of Fees)'
                              : 'User Receives'
                          }>
                          <InputNumber
                            className="input-number"
                            style={{width: '100%'}}
                            disabled
                            formatter={value =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            min={0}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          />
                        </Form.Item>
                      )}
                  </div>
                </Section>

                {!isSimMode && (
                  <Section
                    title="Reason & Attachments"
                    description="Recorded against the transaction for the audit trail.">
                    <ManualTxReasonField
                      form={form}
                      userId={userId ?? user?.split(';')[2]}
                    />
                    <FileUploadList
                      form={form}
                      name="files"
                      label="Files"
                      bucket="campaign-bucket-images"
                    />
                  </Section>
                )}
              </div>

              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.cancel}
                  onClick={onClose}>
                  Cancel
                </button>
                <SubmitButton
                  label={
                    couponState.status === 'checking'
                      ? 'Checking promo code…'
                      : 'Proceed'
                  }
                  disabled={couponBlocksSubmit}
                />
              </div>
            </Form>
          )}
        </div>
      }
    />
  );
};

export default ManualTxModal;
